import React, { useLayoutEffect, useRef, useState } from 'react';
import { useGlide } from '../shared/useGlide.js';

/**
 * A segmented control whose "on" fill springs from option to option. The
 * labels are drawn twice: once in the resting colour, and once inverted (on
 * the accent) in a layer clipped to the active option. Moving the clip moves
 * the fill, and every glyph stays legible mid-slide, which a sliding pill
 * behind black-on-white text does not manage.
 */
export function Segments({ label, value, options, onChange, className = '', itemClassName = '', render = (o) => o.label, ...props }) {
  // Measured on the root, the box the lit layer covers: the active option's
  // offsets include the root's padding, so its far insets must too.
  const rootRef = useRef(null);
  const rect = useActiveRect(rootRef, '[aria-checked="true"]', [value, options.length]);
  const moves = useGlide(rect ? value : null);
  return (
    <div className={`edl-seg ${className}`} role="radiogroup" aria-label={label} ref={rootRef} {...props}>
      <div className="edl-seg__track">
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={`edl-seg__item ${itemClassName}`} onClick={() => onChange(o.value)} title={o.title}>
            {render(o)}
          </button>
        ))}
      </div>
      <div
        className={`edl-seg__lit${moves ? ' is-moving' : ''}`}
        aria-hidden="true"
        style={rect ? { clipPath: `inset(${rect.top}px ${rect.right}px ${rect.bottom}px ${rect.left}px round 100px)` } : { clipPath: 'inset(50% 50% 50% 50%)' }}
      >
        {options.map((o) => (
          <span key={o.value} className={`edl-seg__item ${itemClassName}`}>
            {render(o)}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * The active child's box as insets from the container, kept current through
 * resizes (fonts loading, labels changing). The caller glides only when the
 * active option changes; a remeasure of the same option snaps.
 */
export function useActiveRect(ref, selector, deps) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const place = () => {
      const active = el.querySelector(selector);
      if (!active) {
        setRect(null);
        return;
      }
      const W = el.clientWidth;
      const H = el.clientHeight;
      const r = { left: active.offsetLeft, top: active.offsetTop, width: active.offsetWidth, height: active.offsetHeight };
      const next = { ...r, right: W - r.left - r.width, bottom: H - r.top - r.height };
      setRect((prev) => (
        prev && prev.left === next.left && prev.top === next.top && prev.right === next.right && prev.bottom === next.bottom
          ? prev
          : next
      ));
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(el);
    for (const c of el.children) ro.observe(c);
    return () => ro.disconnect();
  }, deps);
  return rect;
}

/**
 * A hover highlight that follows the pointer between rows of a list, the way
 * a hand moves down it, instead of each row lighting up on its own. It fades
 * in where the pointer enters and glides from there. A remeasure of the row
 * already lit snaps, so a container that's still opening doesn't carry the
 * highlight with it. Vanilla DOM: no render per pointer move. Render
 * `<div ref={glowRef} className="edl-hover-glide" />` as the host's first child.
 */
export function moveHoverGlide(glow, host, row) {
  if (!row) {
    glow.classList.remove('is-on');
    glow._row = null;
    return;
  }
  const hr = host.getBoundingClientRect();
  const rr = row.getBoundingClientRect();
  const visible = glow.classList.contains('is-on');
  const previous = glow._row;
  const glide = visible && previous && previous !== row && previous.isConnected;
  const box = `${rr.left - hr.left + host.scrollLeft},${rr.top - hr.top + host.scrollTop},${rr.width},${rr.height}`;
  // A same-row pass with the box we just wrote (the resize observer's first
  // report) must not cancel a glide that has not been painted yet.
  if (!glide && visible && glow.dataset.box === box) {
    glow._row = row;
    return;
  }
  if (!glide) glow.classList.add('is-jump');
  glow.style.transform = `translate(${rr.left - hr.left + host.scrollLeft}px, ${rr.top - hr.top + host.scrollTop}px)`;
  glow.style.width = `${rr.width}px`;
  glow.style.height = `${rr.height}px`;
  glow.dataset.box = box;
  if (!glide) {
    glow.getBoundingClientRect();
    glow.classList.remove('is-jump');
  }
  glow.classList.add('is-on');
  glow._row = row;
}

export function useHoverGlide(ref, glowRef, selector) {
  useLayoutEffect(() => {
    const host = ref.current;
    const glow = glowRef.current;
    if (!host || !glow) return undefined;
    let current = null;
    const onOver = (e) => {
      const row = e.target.closest?.(selector);
      if (!row || !host.contains(row) || row === current) return;
      current = row;
      moveHoverGlide(glow, host, row);
    };
    const onLeave = () => {
      current = null;
      moveHoverGlide(glow, host, null);
    };
    host.addEventListener('pointerover', onOver);
    host.addEventListener('pointerleave', onLeave);
    return () => {
      host.removeEventListener('pointerover', onOver);
      host.removeEventListener('pointerleave', onLeave);
    };
  }, []);
}
